import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Eye, ShoppingBag, DollarSign } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const aud = (n) => `A$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const monthName = (m) => (m ? new Date(`${m}-01T00:00:00`).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' }) : '');

const Change = ({ now, before }) => {
  if (!before) return <span className="text-xs text-[#1E1622]/40">{now ? 'new' : '–'}</span>;
  const pct = Math.round(((now - before) / before) * 100);
  const up = pct >= 0;
  return <span className={`inline-flex items-center gap-1 text-xs ${up ? 'text-green-700' : 'text-red-600'}`}>{up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}{up ? '+' : ''}{pct}%</span>;
};

const Stat = ({ icon: Icon, label, value, now, before, testId }) => (
  <div className="p-4 rounded-xl bg-[#FAF7F2]" data-testid={testId}>
    <p className="text-xs uppercase tracking-wider text-[#1E1622]/50 flex items-center gap-1.5 mb-1"><Icon size={13} /> {label}</p>
    <p className="font-display text-2xl text-[#1E1622]">{value}</p>
    <Change now={now} before={before} />
  </div>
);

const Row = ({ rank, label, sub, value, change, testId }) => (
  <li className="flex items-center gap-3 py-2.5 border-b border-[#E6DAC5] last:border-0" data-testid={testId}>
    <span className="w-6 text-sm text-[#C5A059] font-medium">{rank}</span>
    <div className="flex-1 min-w-0"><p className="text-sm text-[#1E1622] truncate">{label}</p>{sub && <p className="text-xs text-[#1E1622]/45 truncate">{sub}</p>}</div>
    <span className="text-sm font-medium text-[#1E1622]">{value}</span>
    <span className="w-14 text-right">{change}</span>
  </li>
);

export const AnalyticsSnapshot = ({ authHeaders }) => {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/admin/analytics/snapshot`, authHeaders).then((r) => setD(r.data)).catch(() => toast.error('Could not load the monthly snapshot')); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;
  const t = d.totals;
  return (
    <div className="bg-white rounded-2xl border border-[#E6DAC5] p-6" data-testid="analytics-snapshot">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-5">
        <h3 className="font-semibold text-[#1E1622]">This month at a glance · {monthName(d.month)}</h3>
        <p className="text-xs text-[#1E1622]/50">Compared with {monthName(d.last_month)} · visits recorded on your own site</p>
      </div>
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Stat icon={Eye} label="Page views" value={t.views.toLocaleString('en-AU')} now={t.views} before={t.views_last_month} testId="snapshot-views" />
        <Stat icon={ShoppingBag} label="Orders" value={t.orders} now={t.orders} before={t.orders_last_month} testId="snapshot-orders" />
        <Stat icon={DollarSign} label="Revenue" value={aud(t.revenue)} now={t.revenue} before={t.revenue_last_month} testId="snapshot-revenue" />
      </div>
      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <p className="text-sm font-medium text-[#1E1622] mb-2">Top 10 pages</p>
          {d.top_pages.length ? (
            <ol data-testid="snapshot-top-pages">{d.top_pages.map((p, i) => <Row key={p.path} rank={i + 1} label={p.title?.split(' | ')[0] || p.path} sub={p.path} value={p.views.toLocaleString('en-AU')} change={<Change now={p.views} before={p.last_month} />} testId={`snapshot-page-${i}`} />)}</ol>
          ) : <p className="text-sm text-[#1E1622]/50" data-testid="snapshot-no-pages">No visits recorded yet this month. Visits start counting from now.</p>}
        </div>
        <div>
          <p className="text-sm font-medium text-[#1E1622] mb-2">Top 5 best-selling guides</p>
          {d.top_products.length ? (
            <ol data-testid="snapshot-top-products">{d.top_products.map((p, i) => <Row key={p.title} rank={i + 1} label={p.title} sub={`${p.units} sold`} value={aud(p.revenue)} change={<Change now={p.revenue} before={p.last_month_revenue} />} testId={`snapshot-product-${i}`} />)}</ol>
          ) : <p className="text-sm text-[#1E1622]/50" data-testid="snapshot-no-sales">No sales yet this month.</p>}
        </div>
      </div>
    </div>
  );
};
